// ABOUTME: Browser/device capability detection for the reel chrome (SPEC-26 Phase 4b).
// ABOUTME: Feature-detected via matchMedia/CSS.supports; the user-agent is a tie-breaker, never the test.

// Which authored composition to render is decided in `useViewportSize`'s `resolveComposition` — by
// viewport width, gated on this module's `mobileUA` (Phase 6: a non-phone stays on the desktop
// composition at any width). What lives here is the orthogonal question: what can the browser in
// front of us actually do. The two stay separate because they answer different questions and get them
// wrong in different ways:
//
//   • A desktop browser resized narrow-and-tall keeps the desktop composition (it is not a phone) but
//     may still legitimately lack :hover (a touchscreen laptop) — capability and composition disagree.
//   • An iPad in landscape gets the desktop composition (its UA is not a phone's) but DOES need touch
//     hit targets and must not rely on :hover (capability).
//
// Every field below is derived from a feature test that every browser we support implements, with a
// literal `false` for anything unknown — a missing capability must never read as "present".
//
// AC-10: nothing brand-, chapter- or product-specific appears in this file. It is generic.

const QUERY_COARSE_POINTER = '(pointer: coarse)';
const QUERY_NO_HOVER = '(hover: none)';
const QUERY_REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const QUERY_STANDALONE = '(display-mode: standalone)';

/** `matchMedia` guarded for jsdom (which has it) and for the handful of engines that throw on an
 *  unknown feature query rather than returning a non-matching list. */
function mq(query) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return Boolean(window.matchMedia(query).matches);
  } catch {
    return false;
  }
}

function cssSupports(property, value) {
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') return false;
  try {
    return CSS.supports(property, value);
  } catch {
    return false;
  }
}

function userAgent() {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent || '';
}

function maxTouchPoints() {
  if (typeof navigator === 'undefined') return 0;
  return Number(navigator.maxTouchPoints) || 0;
}

/**
 * iPadOS 13+ reports itself as `Macintosh; Intel Mac OS X` with no iPad token at all, so a UA regex
 * alone classifies every modern iPad as a desktop Mac. The reliable tell is a "Mac" platform that
 * also reports touch points — no real Mac does, including ones with a Magic Trackpad.
 */
function detectIOS() {
  const ua = userAgent();
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  const platform = (typeof navigator !== 'undefined' && navigator.platform) || '';
  return platform === 'MacIntel' && maxTouchPoints() > 1;
}

/**
 * Safari only — Chrome, Edge, Firefox and every iOS browser wrapping WebKit also carry "Safari" in
 * their UA, so this excludes them by name. Used for capability *workarounds* (see `index.css`), never
 * to decide what to render.
 */
function detectSafari() {
  const ua = userAgent();
  return /Safari/.test(ua) && !/Chrome|Chromium|Android|CriOS|FxiOS|EdgiOS|Edg\//.test(ua);
}

/**
 * A best-effort "this is a phone/tablet" hint. Chromium exposes it as a real client hint;
 * everything else falls back to the UA string. Consumers must treat it as a hint — `touch` and
 * `coarsePointer` are the load-bearing signals.
 */
function detectMobileUA() {
  if (typeof navigator !== 'undefined' && navigator.userAgentData
      && typeof navigator.userAgentData.mobile === 'boolean') {
    return navigator.userAgentData.mobile;
  }
  return /Mobi|Android|iPhone|iPod|IEMobile|Opera Mini/.test(userAgent());
}

/**
 * The physical display's short and long side in CSS px, orientation-invariant.
 *
 * SPEC-26 Phase 5 needs a phone-vs-tablet answer, and no capability tells the two apart — they are
 * identical on pointer, hover and touch, and iPadOS 13+ does not even admit to being an iPad. The
 * only discriminator is size, and the *screen* is a better source for it than the layout viewport:
 *
 *   • iPad Split View gives the browser a ~507px-wide window on an 834px-wide device. Measured by
 *     viewport that is a phone; measured by screen it is the tablet it actually is.
 *   • Android split-screen and a collapsing URL bar move the viewport the same way.
 *
 * min/max rather than width/height because iOS Safari historically does not swap `screen.width` and
 * `screen.height` on rotation while Chromium does — taking both ends makes that difference moot.
 * 0 where the API is unavailable, so callers fall back to the viewport rather than trusting a zero.
 */
function screenSides() {
  if (typeof window === 'undefined' || !window.screen) return { short: 0, long: 0 };
  const width = Number(window.screen.width) || 0;
  const height = Number(window.screen.height) || 0;
  if (!width || !height) return { short: 0, long: 0 };
  return { short: Math.min(width, height), long: Math.max(width, height) };
}

/** @returns {{coarsePointer:boolean, noHover:boolean, touch:boolean, touchPoints:number,
 *             mobileUA:boolean, ios:boolean, android:boolean, safari:boolean, standalone:boolean,
 *             reducedMotion:boolean, backdropFilter:boolean, dynamicViewportUnits:boolean,
 *             screenShortSide:number, screenLongSide:number}} */
export function readDeviceProfile() {
  const coarsePointer = mq(QUERY_COARSE_POINTER);
  const touchPoints = maxTouchPoints();
  const screen = screenSides();
  return {
    coarsePointer,
    screenShortSide: screen.short,
    screenLongSide: screen.long,
    noHover: mq(QUERY_NO_HOVER),
    // Either signal alone has a false negative: a touchscreen laptop reports a fine pointer, and
    // some Android WebViews report zero touch points while clearly being touch-driven.
    touch: coarsePointer || touchPoints > 0,
    touchPoints,
    mobileUA: detectMobileUA(),
    ios: detectIOS(),
    android: /Android/.test(userAgent()),
    safari: detectSafari(),
    standalone: mq(QUERY_STANDALONE),
    reducedMotion: mq(QUERY_REDUCED_MOTION),
    // Firefox shipped `backdrop-filter` unprefixed in 103; Safari needs the -webkit- form to this
    // day. The whole reel chrome is frosted glass, so a browser without it needs the opaque
    // fallback in index.css — this is what tells the UI whether that is in play.
    backdropFilter: cssSupports('backdrop-filter', 'blur(1px)')
      || cssSupports('-webkit-backdrop-filter', 'blur(1px)'),
    dynamicViewportUnits: cssSupports('height', '100dvh'),
  };
}

// One snapshot shared by every subscriber, replaced only when something actually changed, so
// `useSyncExternalStore`'s Object.is check bails components out of a pointless re-render. Capability
// changes are rare but real: plugging in a mouse flips `coarsePointer`, and rotating a 2-in-1 into
// tablet mode flips both pointer queries.
let snapshot = readDeviceProfile();
const listeners = new Set();
let mediaLists = null;

const SERVER_SNAPSHOT = Object.freeze({
  coarsePointer: false, noHover: false, touch: false, touchPoints: 0, mobileUA: false,
  ios: false, android: false, safari: false, standalone: false, reducedMotion: false,
  backdropFilter: true, dynamicViewportUnits: true, screenShortSide: 0, screenLongSide: 0,
});

function sameProfile(a, b) {
  return Object.keys(a).every((key) => a[key] === b[key]);
}

function handleChange() {
  const next = readDeviceProfile();
  if (sameProfile(snapshot, next)) return;
  snapshot = next;
  listeners.forEach((listener) => listener());
}

/** Safari < 14 and older Edge only implement the deprecated `addListener` on a MediaQueryList. */
function listen(list) {
  if (typeof list.addEventListener === 'function') {
    list.addEventListener('change', handleChange);
    return () => list.removeEventListener('change', handleChange);
  }
  if (typeof list.addListener === 'function') {
    list.addListener(handleChange);
    return () => list.removeListener(handleChange);
  }
  return () => {};
}

export function subscribeDeviceProfile(listener) {
  listeners.add(listener);
  if (!mediaLists && typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    mediaLists = [QUERY_COARSE_POINTER, QUERY_NO_HOVER, QUERY_REDUCED_MOTION, QUERY_STANDALONE]
      .map((query) => {
        try {
          return listen(window.matchMedia(query));
        } catch {
          return () => {};
        }
      });
  }
  handleChange();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && mediaLists) {
      mediaLists.forEach((unlisten) => unlisten());
      mediaLists = null;
    }
  };
}

export function getDeviceProfileSnapshot() {
  return snapshot;
}

export function getDeviceProfileServerSnapshot() {
  return SERVER_SNAPSHOT;
}

/** Test seam — resets the module snapshot so a suite can change `matchMedia` between cases. */
export function __resetDeviceProfileForTests() {
  snapshot = readDeviceProfile();
}
